import React, { useCallback, useEffect, useState } from 'react';
import { Archive, Loader2 } from 'lucide-react';
import { getHistoricalQuotesApi, createJobForQuoteApi, HISTORICAL_QUOTES_PER_PAGE } from '../../services/quoteApi';
import { getCustomerVehiclesApi, addCustomerVehicleApi } from '../../services/customerVehicleApi';

const money = (v) => (v == null ? '—' : `$${Number(v).toFixed(2)}`);
const day = (d) => (d ? new Date(d).toLocaleDateString('en-AU',
    { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

/**
 * Quotes brought across from Machanicdesk.
 *
 * 🔑 Why these are not in the lanes above: that board is Jobs carrying `quote = true` —
 * priced work this product raised, already a job, moving through lanes as the customer
 * responds. These are Quote rows from another product: a record of what was once quoted,
 * with no job, no lanes and no lifecycle. Putting them in the lanes would drop closed
 * 2017 work onto a board meant to show what needs doing now.
 *
 * So: its own section, collapsed by default, read-only apart from the one action that
 * matters — turning a quote into a job when the workshop decides to do the work.
 */
export default function HistoricalQuotes() {
    const [data, setData] = useState(null);      // null = not loaded yet
    const [error, setError] = useState(null);
    const [busyUuid, setBusyUuid] = useState(null);
    const [page, setPage] = useState(0);
    const [form, setForm] = useState({ status: '', from: '', to: '', customer: '' });
    /*
     * 🔑 null means NO SEARCH HAS BEEN RUN, and nothing is fetched in that state — it is
     * not the same as "searched and found nothing", which is why this is not `{}`.
     *
     * The list used to load its first page on mount. Ten thousand quotes spanning years
     * open on the newest ten, which are almost never the ten anyone came for, and the
     * screen reads as a feed of old work rather than a thing you ask a question of. A
     * date is now required before anything is fetched.
     */
    const [applied, setApplied] = useState(null);
    // Shown only after someone presses Search with both bounds empty. Saying it up front
    // would be scolding them for a mistake they have not made yet.
    const [dateRequired, setDateRequired] = useState(false);
    // 🔑 Distinct from "data is null". A search over ten thousand rows takes long enough
    // to look broken, and the rows already on screen stay there while it runs — without
    // this the only feedback is that nothing appears to happen.
    const [searching, setSearching] = useState(false);

    const load = useCallback(async (which = page, range = applied) => {
        if (!range) return;   // nothing asked for yet
        setSearching(true);
        try {
            const raw = await getHistoricalQuotesApi({ ...range, page: which });
            // 🪤 Tolerate the OLD unpaged shape. This endpoint returned a plain array
            // before it was paged, and a browser holding the new bundle against a server
            // that has not been restarted gets a 200 with that array — so data.items is
            // undefined, the list renders empty and the count reads 0. No error, no clue,
            // and it looks exactly like "you have no imported quotes" when there are
            // ten thousand. Same guard the imports list already carries.
            setData(Array.isArray(raw)
                ? { items: raw, page: 0, size: raw.length, total: raw.length, totalPages: 1 }
                : raw);
            setError(null);
        } catch {
            setData({ items: [], page: 0, size: 0, total: 0, totalPages: 0 });
            setError('Could not load imported quotes.');
        } finally {
            setSearching(false);
        }
    }, [page, applied]);

    useEffect(() => { if (applied) load(page, applied); }, [page, applied, load]);

    const search = () => {
        // One bound is enough — "everything since we migrated" and "everything before
        // 2020" are both real questions. Neither is the unbounded list.
        //
        // 🔑 A customer name does NOT stand in for a date (asked and confirmed
        // 2026-09-05). It narrows a window; it does not replace one. "Every quote we
        // ever wrote for Smith" is still an unbounded scan of 10,589 rows.
        if (!form.from && !form.to) { setDateRequired(true); return; }
        setDateRequired(false);
        setPage(0);
        setApplied(form);
    };

    const clear = () => {
        setForm({ status: '', from: '', to: '', customer: '' });
        setDateRequired(false);
        setPage(0);
        setApplied(null);
        setData(null);        // back to the prompt, not to an empty result
        setError(null);
    };

    const searched = applied !== null;
    const items = data?.items ?? [];
    const total = data?.total ?? 0;
    const size = data?.size || HISTORICAL_QUOTES_PER_PAGE;
    const first = total === 0 ? 0 : page * size + 1;
    const last = Math.min((page + 1) * size, total);

    return (
        <div className="flex-1 overflow-auto p-4">
            <div className="bg-white rounded-2xl border border-gray-200">
                <div className="px-5 py-4 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                        <Archive size={15} className="text-gray-400" />
                        <h3 className="font-bold text-gray-900">Imported quotes</h3>
                        {/* ⚠️ Says WHICH number this is. Filtered, it is a match count,
                            and "0 from Machanicdesk" beside a workshop holding ten
                            thousand of them reads as "you have none". */}
                        {/* ⚠️ A match count, never a total: nothing counts the whole
                            history any more, and printing one here would be a number
                            this component cannot stand behind. */}
                        {searched && data && (
                            <span className="text-xs text-gray-400">
                                {total.toLocaleString()} matching
                            </span>
                        )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                        A record of what was quoted before the migration. Raising a job puts
                        the quote back in front of the customer — it moves to Active, and
                        their approval turns it into a job with a billable invoice.
                    </p>

                    {/* 🔑 A date filter, and only here. The Active board is two short
                        lanes you can read at a glance; this is thousands of rows spanning
                        years, where "quotes from around then" is the only way in. It
                        filters the quote's OWN date, not when it was imported — that is
                        one instant for every row and would narrow nothing. */}
                    <div className="flex flex-wrap items-end gap-2 mt-3">
                        <div>
                            <label htmlFor="hq-status" className="block text-[11px] font-medium text-gray-500 mb-1">Status</label>
                            <select id="hq-status" value={form.status}
                                onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))}
                                className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500">
                                <option value="">Any status</option>
                                {/* The import mapped Machanicdesk's own words onto these:
                                    "invoice created" and "job created" both became
                                    APPROVED, so that is where the acted-on quotes are. */}
                                <option value="APPROVED">Approved</option>
                                <option value="SENT">Sent</option>
                                <option value="DRAFT">Draft</option>
                                <option value="DECLINED">Declined</option>
                                <option value="SUPERSEDED">Superseded</option>
                            </select>
                        </div>
                        <div>
                            <label htmlFor="hq-from" className="block text-[11px] font-medium text-gray-500 mb-1">From</label>
                            <input id="hq-from" type="date" value={form.from}
                                onChange={(e) => setForm(f => ({ ...f, from: e.target.value }))}
                                className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500" />
                        </div>
                        <div>
                            <label htmlFor="hq-to" className="block text-[11px] font-medium text-gray-500 mb-1">To</label>
                            <input id="hq-to" type="date" value={form.to}
                                onChange={(e) => setForm(f => ({ ...f, to: e.target.value }))}
                                className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500" />
                        </div>
                        <div>
                            <label htmlFor="hq-customer" className="block text-[11px] font-medium text-gray-500 mb-1">Customer</label>
                            {/* Matches a business name OR a personal one, because
                                Customer.getFullName() reads from different columns for
                                each and most of a workshop's book is businesses. */}
                            <input id="hq-customer" type="search" value={form.customer}
                                onChange={(e) => setForm(f => ({ ...f, customer: e.target.value }))}
                                onKeyDown={(e) => { if (e.key === 'Enter') search(); }}
                                maxLength={100} placeholder="Name contains…"
                                className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500" />
                        </div>
                        <button type="button" onClick={search} disabled={searching}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 disabled:opacity-50">
                            {searching && <Loader2 size={13} className="animate-spin" />}
                            {searching ? 'Searching…' : 'Search'}
                        </button>
                        {searched && (
                            <button type="button" onClick={clear}
                                className="text-sm text-gray-500 hover:text-gray-700">Clear</button>
                        )}
                    </div>
                    {dateRequired && (
                        <p className="text-[11px] text-amber-700 mt-2">
                            Choose a From or To date to search — a customer name narrows a date range, it does not replace one.
                        </p>
                    )}
                    {searched && (
                        <p className="text-[11px] text-violet-700 mt-2">
                            {/* Said out loud, because it is a real exclusion — and with a
                                date now required, it applies to every result on screen. */}
                            A quote whose export carried no date is not in this list.
                        </p>
                    )}
                </div>

                {/* Shown while ANY load runs, not only the first: paging and filtering
                    over ten thousand rows is slow enough to look broken otherwise. */}
                {searching && (
                    <p className="px-5 py-6 text-sm text-gray-400 flex items-center gap-2">
                        <Loader2 size={15} className="animate-spin" />
                        Loading imported quotes…
                    </p>
                )}
                {error && <p className="px-5 py-3 text-sm text-amber-700">{error}</p>}

                {!searched && !error && (
                    <p className="px-5 py-6 text-sm text-gray-400">
                        Pick a date range and press Search. Quotes brought in from
                        Machanicdesk go back years, so this list starts empty rather
                        than opening on whichever ten happen to be newest.
                    </p>
                )}

                {searched && data && !searching && items.length === 0 && !error && (
                    <p className="px-5 py-6 text-sm text-gray-400">
                        No imported quotes in that range.
                    </p>
                )}

                {!searching && items.length > 0 && (
                    <div className="divide-y divide-gray-100">
                        {items.map((q) => (
                            <HistoricalQuoteRow key={q.uuid} quote={q}
                                busy={busyUuid === q.uuid} onBusy={setBusyUuid}
                                onDone={() => load(page, applied)} />
                        ))}
                    </div>
                )}

                {data && data.totalPages > 1 && (
                    <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-gray-100">
                        <p className="text-xs text-gray-500">
                            {first.toLocaleString()}–{last.toLocaleString()} of {total.toLocaleString()}
                        </p>
                        <div className="flex items-center gap-2">
                            <button type="button" onClick={() => setPage((p) => p - 1)}
                                disabled={page === 0}
                                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                                Newer
                            </button>
                            <button type="button" onClick={() => setPage((p) => p + 1)}
                                disabled={page >= data.totalPages - 1}
                                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                                Older
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

/**
 * One imported quote, and the vehicle question that must be answered first.
 *
 * ⚠️ job.vehicle_id is NOT NULL and a migrated quote names its customer but not their
 * car. Existing vehicles are offered when there are any — preselected when there is
 * exactly one, which is a confirmation rather than a choice — and the rego field is
 * always present, because most migrated customers own no vehicle at all.
 */
function HistoricalQuoteRow({ quote, busy, onBusy, onDone }) {
    const [picking, setPicking] = useState(false);
    const [vehicles, setVehicles] = useState(null);
    const [chosen, setChosen] = useState('');
    const [rego, setRego] = useState('');
    const [error, setError] = useState(null);

    const startPicking = async () => {
        setPicking(true);
        setError(null);
        if (vehicles !== null || !quote.customerUuid) return;
        try {
            const list = await getCustomerVehiclesApi(quote.customerUuid);
            setVehicles(list);
            if (list.length === 1) setChosen(list[0].uuid);
        } catch {
            setVehicles([]);
        }
    };

    const submit = async () => {
        onBusy(quote.uuid);
        setError(null);
        try {
            let vehicleUuid = chosen;
            if (!vehicleUuid && rego.trim()) {
                const added = await addCustomerVehicleApi(quote.customerUuid,
                    { regoNumber: rego.trim() });
                vehicleUuid = added.uuid;
            }
            if (!vehicleUuid) {
                setError('Choose a vehicle, or add one by registration number.');
                return;
            }
            await createJobForQuoteApi(quote.uuid, vehicleUuid);
            setPicking(false);
            onDone();
        } catch (err) {
            setError(err?.response?.data?.message || 'Could not create the job.');
        } finally {
            onBusy(null);
        }
    };

    return (
        <div className="px-5 py-3">
            <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900">
                        <span className="font-mono text-gray-500 mr-2">#{quote.quoteNumber}</span>
                        {quote.customerName || 'No customer'}
                    </p>
                    <p className="text-xs text-gray-500">
                        {day(quote.quoteDate)} · {money(quote.totalAmount)}
                        {quote.status && <span className="ml-2 text-gray-400">{quote.status}</span>}
                    </p>
                </div>

                {quote.jobUuid ? (
                    <span className="shrink-0 text-xs text-emerald-700 font-medium">
                        Job {quote.jobNumber}
                    </span>
                ) : !picking ? (
                    <button type="button" onClick={startPicking}
                        className="shrink-0 text-xs font-semibold text-violet-600 hover:text-violet-700">
                        Activate
                    </button>
                ) : null}
            </div>

            {picking && !quote.jobUuid && (
                <div className="mt-3 space-y-2 border-l-2 border-violet-200 pl-3">
                    <p className="text-xs font-medium text-gray-700">
                        Which vehicle was this quoted for?
                    </p>
                    {/* 🔑 Says where it GOES. "Create job" was accurate about the row
                        written and wrong about what happens: it is raised as a quote,
                        lands under Active, and only the customer's approval turns it
                        into a job. */}
                    <p className="text-[11px] text-gray-500">
                        Activating moves this quote to <strong className="font-medium">Active</strong>,
                        where you can send it for approval.
                    </p>

                    {vehicles === null && <p className="text-xs text-gray-400">Loading vehicles…</p>}

                    {vehicles?.map((v) => (
                        <label key={v.uuid} className="flex items-center gap-2 text-sm cursor-pointer">
                            <input type="radio" name={`veh-${quote.uuid}`}
                                checked={chosen === v.uuid}
                                onChange={() => { setChosen(v.uuid); setRego(''); }} />
                            <span className="font-mono">{v.regoNumber || '—'}</span>
                            <span className="text-gray-500">{v.displayName}</span>
                        </label>
                    ))}

                    <input type="text" value={rego}
                        onChange={(e) => { setRego(e.target.value.toUpperCase()); setChosen(''); }}
                        placeholder={vehicles?.length ? 'Or add another by rego' : 'Add the vehicle by rego'}
                        className="w-full px-2 py-1 border border-gray-200 rounded-lg text-sm font-mono uppercase focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none" />

                    {error && <p className="text-[11px] text-red-700">{error}</p>}

                    <div className="flex items-center gap-2">
                        <button type="button" onClick={submit}
                            disabled={busy || (!chosen && !rego.trim())}
                            className="text-xs font-semibold bg-violet-600 text-white px-2.5 py-1 rounded-lg hover:bg-violet-700 disabled:opacity-50">
                            {busy ? <Loader2 size={12} className="animate-spin" /> : 'Activate'}
                        </button>
                        <button type="button" onClick={() => { setPicking(false); setError(null); }}
                            className="text-xs text-gray-500 hover:text-gray-700">Cancel</button>
                    </div>
                    <p className="text-[11px] text-gray-500">
                        Each priced line becomes a work item. Lines at $0.00 do not.
                    </p>
                </div>
            )}
        </div>
    );
}
